import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listVisitServices, createVisitService } from '../../api/visits.js'
import { Card, CardHeader } from '../../components/Card.jsx'
import ErrorBanner from '../../components/ErrorBanner.jsx'

// In-app capture of ICER visit services (home / site / outreach), replacing the
// Django-admin entry. total_pgk is computed server-side from the rate +
// modifiers and credited to the patient's / organisation's value delivered.
const TYPES = [
  ['home_visit', 'Home visit'],
  ['site_visit', 'Workplace / site visit'],
  ['outreach', 'Outreach'],
]
const EMPTY = {
  visit_type: 'home_visit',
  patient: '',
  organization: '',
  base_rate_pgk: '',
  travel_pgk: '',
  after_hours_pgk: '',
  distance_km: '',
  distance_rate_pgk: '',
  notes: '',
}

export default function VisitServiceEntry() {
  const qc = useQueryClient()
  const [form, setForm] = useState(EMPTY)
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }))

  const recent = useQuery({
    queryKey: ['visit-services'],
    queryFn: () => listVisitServices({ ordering: '-created_at' }),
  })

  const num = (v) => {
    const n = parseFloat(v)
    return Number.isNaN(n) ? 0 : n
  }
  const preview =
    num(form.base_rate_pgk) +
    num(form.travel_pgk) +
    num(form.after_hours_pgk) +
    num(form.distance_km) * num(form.distance_rate_pgk)

  const mut = useMutation({
    mutationFn: createVisitService,
    onSuccess: () => {
      setForm(EMPTY)
      qc.invalidateQueries({ queryKey: ['visit-services'] })
    },
  })

  function submit() {
    const p = { visit_type: form.visit_type }
    if (form.patient) p.patient = parseInt(form.patient, 10)
    if (form.organization) p.organization = parseInt(form.organization, 10)
    for (const k of [
      'base_rate_pgk',
      'travel_pgk',
      'after_hours_pgk',
      'distance_km',
      'distance_rate_pgk',
    ]) {
      const v = parseFloat(form[k])
      if (!Number.isNaN(v)) p[k] = v
    }
    if (form.notes.trim()) p.notes = form.notes.trim()
    mut.mutate(p)
  }

  const Field = ({ label, k, ph }) => (
    <label className="text-xs text-slate-600 flex flex-col gap-1">
      {label}
      <input
        className="border rounded px-2 py-1.5 text-sm"
        type="number"
        placeholder={ph}
        value={form[k]}
        onChange={set(k)}
      />
    </label>
  )

  return (
    <div className="max-w-3xl mx-auto p-4 space-y-4">
      <Card>
        <CardHeader
          title="Record a visit service"
          subtitle="Home / workplace / outreach visits delivered by the ICER team. The total (base + travel + after-hours + distance × per-km) is computed on save and credited to the patient's or organisation's value delivered."
        />
        <div className="grid gap-3 sm:grid-cols-2 mt-3">
          <label className="text-xs text-slate-600 flex flex-col gap-1">
            Visit type
            <select
              className="border rounded px-2 py-1.5 text-sm"
              value={form.visit_type}
              onChange={set('visit_type')}
            >
              {TYPES.map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <div className="hidden sm:block" />
          <Field label="Patient ID (for home visits)" k="patient" ph="e.g. 175" />
          <Field
            label="Organisation ID (for site / outreach)"
            k="organization"
            ph="e.g. 2 (NYDA)"
          />
          <Field label="Base rate (PGK)" k="base_rate_pgk" ph="e.g. 550" />
          <Field label="Travel (PGK)" k="travel_pgk" />
          <Field label="After-hours (PGK)" k="after_hours_pgk" />
          <Field label="Distance (km)" k="distance_km" />
          <Field label="Per-km rate (PGK)" k="distance_rate_pgk" />
        </div>
        <label className="text-xs text-slate-600 flex flex-col gap-1 mt-3">
          Notes
          <textarea
            className="border rounded px-2 py-1.5 text-sm"
            rows={2}
            value={form.notes}
            onChange={set('notes')}
          />
        </label>
        {mut.isError && (
          <div className="mt-2">
            <ErrorBanner error={mut.error} />
          </div>
        )}
        <div className="mt-3 flex items-center justify-between">
          <div className="text-sm text-slate-600">
            Total:{' '}
            <span className="font-semibold tabular-nums">
              K {preview.toFixed(2)}
            </span>
          </div>
          <button
            onClick={submit}
            disabled={mut.isPending}
            className="text-sm bg-wantok-700 hover:bg-wantok-800 text-white rounded px-3 py-2 disabled:opacity-60"
          >
            {mut.isPending ? 'Saving…' : 'Record visit'}
          </button>
        </div>
      </Card>

      <Card>
        <CardHeader title="Recent visits" />
        {recent.isLoading ? (
          <div className="text-sm text-slate-500">Loading…</div>
        ) : (
          <table className="w-full text-sm">
            <tbody>
              {(recent.data || []).slice(0, 15).map((v) => (
                <tr key={v.id} className="border-t border-sand-100">
                  <td className="py-1.5">{v.visit_type_display}</td>
                  <td className="text-slate-500">
                    {v.patient_name || v.organization_name || '—'}
                  </td>
                  <td className="text-right tabular-nums">
                    K {Number(v.total_pgk).toFixed(2)}
                  </td>
                </tr>
              ))}
              {(recent.data || []).length === 0 && (
                <tr>
                  <td className="py-3 text-slate-500 text-sm">
                    No visits recorded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  )
}
